import { gql, useLazyQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment } from '@mui/material';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { UserContext } from '@providers/userContextProvider';
import { CircularProgress } from 'hephaestus/web';
import { debounce } from 'lodash-es';
import { useCallback, useContext, useEffect, useState } from 'react';

const SEARCH_DEBOUNCE_IN_MS = 500;

type OrganizationUser = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: {
    translatedName: TranslatedString;
  };
};

type SearchResults = {
  organizationUsers: OrganizationUser[];
};

export const SEARCH_ORGANIZATION_USERS = gql`
  query fetchOrganizationUsers($input: FetchOrganizationUsersInput!) {
    fetchOrganizationUsers(input: $input) {
      organizationUsers {
        id
        firstName
        lastName
        email
        role {
          translatedName { ${TRANSLATED_STRING_FIELDS} }
        }
      }
    }
  }
`;

export const FETCH_ORGANIZATION_USER = gql`
  query fetchOrganizationUser($organizationUserId: ObjectID!) {
    fetchOrganizationUser(organizationUserId: $organizationUserId) {
      organizationUser {
        id
        firstName
        lastName
        email
        role {
          translatedName { ${TRANSLATED_STRING_FIELDS} }
        }
      }
    }
  }
`;

export const OrganizationUserSelect = ({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (value?: OrganizationUser) => void;
  label?: string;
}) => {
  const { activeOrganization } = useContext(UserContext);
  const [searchResult, setSearchResult] = useState<SearchResults>({
    organizationUsers: [],
  });
  const [selectedUser, setSelectedUser] = useState<OrganizationUser | null>(
    null,
  );

  const [fetchOrganizationUsers, { loading }] = useLazyQuery(
    SEARCH_ORGANIZATION_USERS,
  );
  const [fetchOrganizationUser] = useLazyQuery(FETCH_ORGANIZATION_USER);

  useEffect(() => {
    if (value) {
      fetchOrganizationUser({
        fetchPolicy: 'no-cache',
        variables: { organizationUserId: value },
        onCompleted: (data) => {
          if (data?.fetchOrganizationUser) {
            setSelectedUser(data.fetchOrganizationUser.organizationUser);
          }
        },
      });
    }
  }, [value, fetchOrganizationUser]);

  const handleInputChange = (v: string) => {
    if (v === '') {
      setSearchResult({
        organizationUsers: [],
      });
    }

    fetchOrganizationUsers({
      variables: {
        input: {
          filter: { searchText: v, organizationId: activeOrganization.id },
        },
      },
      onCompleted: (data) => {
        const users = data?.fetchOrganizationUsers?.organizationUsers;
        if (users) {
          setSearchResult({
            organizationUsers: users,
          });
        } else {
          setSearchResult({
            organizationUsers: [],
          });
        }
      },
      onError: () => {
        setSearchResult({
          organizationUsers: [],
        });
      },
    });
  };

  const postponedCall = useCallback(
    debounce((v) => {
      if (v) {
        handleInputChange(v);
      }
    }, SEARCH_DEBOUNCE_IN_MS),
    [],
  );

  return (
    <Autocomplete
      testId='organizationUserSelect'
      label={label ?? ''}
      freeSolo
      loading={loading}
      options={searchResult.organizationUsers}
      isOptionEqualToValue={(option: OrganizationUser, v: OrganizationUser) =>
        option.id === v.id
      }
      onInputChange={(_e: React.SyntheticEvent, v: string) => postponedCall(v)}
      getOptionLabel={(option: OrganizationUser) =>
        `${option?.firstName} ${option?.lastName}`
      }
      renderOption={(props: any, option: OrganizationUser) => (
        <MenuItem key={option.id} {...props}>
          {`${option.firstName} ${option.lastName} - ${option.email} - ${translateBackend(option.role.translatedName)}`}
        </MenuItem>
      )}
      fullWidth
      filterOptions={(options: OrganizationUser[]) => options}
      value={selectedUser}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position='end'>
            {loading ? <CircularProgress color='inherit' size='large' /> : null}
          </InputAdornment>
        ),
      }}
      onChange={(
        _e: React.SyntheticEvent,
        newValue: OrganizationUser | null,
      ) => {
        setSelectedUser(newValue);
        onChange(newValue ?? undefined);
      }}
    />
  );
};
